/**
 * What the task register is being asked for: the filter row, and the type that
 * names the set.
 *
 * The filters travel as ONE object rather than as a value and a setter per
 * control, the way `CallFilterMenu` takes `CallFilters`. A pair of props per
 * control is the arrangement where somebody adds the next one and forgets to
 * thread it through, and the object is also the honest description of what a
 * page of this list depends on - `TasksList` resets to page one on exactly these
 * fields.
 *
 * `q` is what the box holds RIGHT NOW, not what the list was last loaded with:
 * the debounce lives in `TasksList`, because the delay is a property of the
 * request, not of the input. The committed term is the one in the address bar;
 * the buffer never goes there, or the URL would change once per keystroke.
 */
import { useMemo, type ReactNode } from "react";
import { useT } from "../../../i18n/I18nProvider";
import type { Project, TaskPriority, TaskStatus } from "../../../lib/projects";
import { staffOptionLabel } from "../../../lib/staff";
import { MenuField, PickerField, PillTabs, SearchField, Toolbar, ToolbarGap } from "../../ui";
import { useCrmUser } from "../../shell/CrmUser";
import { useStaff } from "../_shared/staff";
import { TASK_STATUSES } from "../projects/status";
import { PRIORITIES } from "./priority";
import { useTaskTags } from "./taskTags";

/** The value every "no opinion" filter carries, and the one id no person has. */
export const ALL = "all";

export type StatusFilter = TaskStatus | typeof ALL;
export type PriorityFilter = TaskPriority | typeof ALL;

/**
 * The register as a list, or the same records grouped by status.
 *
 * This array is the one place that says which values are legal: the pills below
 * are built from it, and so is the `?view=` parsing in `TasksList`. A third view
 * is added here once, rather than in two lists that can disagree about what an
 * unknown value falls back to.
 */
export const VIEWS = ["list", "board"] as const;

export type View = (typeof VIEWS)[number];

/** A `Record`, not a ternary: a view added to `VIEWS` without a label is then a
 *  type error rather than a pill with nothing written on it. */
const VIEW_LABEL: Record<View, string> = {
  list: "tasks.viewList",
  board: "tasks.viewBoard",
};

export interface TaskFilters {
  /** The raw search box, debounced by the list before it becomes a request. */
  q: string;
  status: StatusFilter;
  priority: PriorityFilter;
  /** A project id, or `ALL`. */
  projectId: string;
  /**
   * A person's id, or `ALL`. The endpoint has taken `assignee` since the module
   * was written (`where.assignees = { has: ... }`) and no screen ever sent it,
   * so this filter is the first caller the parameter has ever had.
   */
  assignee: string;
  /**
   * One tag, or the empty string for no opinion - and NOT `ALL` like its four
   * neighbours.
   *
   * The sentinel has to be a value the field can never legitimately hold. A
   * project id and a person id are cuids, so "all" is safely outside their sets;
   * a tag is free text an operator types, and somebody labelling a batch of
   * tasks `all` is not a strange thing to do. With `ALL` as the sentinel that
   * tag would be unfilterable and, worse, indistinguishable from no filter at
   * all. The empty string cannot collide: the API drops blank entries on the way
   * in (`normalizeTags`), so no stored tag is one.
   *
   * Matched with array containment, exactly, which is why the control is a menu
   * fed from `GET /task-tags` rather than a text box - "no such tag" and "no
   * task carries that tag" are different answers and a typed box cannot tell
   * them apart.
   */
  tag: string;
}

export const DEFAULT_TASK_FILTERS: TaskFilters = {
  q: "",
  status: ALL,
  priority: ALL,
  projectId: ALL,
  assignee: ALL,
  tag: "",
};

export function TasksToolbar({
  filters,
  onFilters,
  projects,
  view,
  onView,
}: {
  filters: TaskFilters;
  onFilters: (next: TaskFilters) => void;
  /** The project filter's options, loaded once by the list. */
  projects: Project[];
  view: View;
  onView: (v: View) => void;
}): ReactNode {
  const t = useT();
  const me = useCrmUser();
  const { staff } = useStaff();
  const tags = useTaskTags();
  const set = (patch: Partial<TaskFilters>) => onFilters({ ...filters, ...patch });

  /**
   * Anyone, then me, then everybody else.
   *
   * "My tasks" is an OPTION of the assignee filter and not a toggle beside it,
   * which is where the support queue puts its equivalent. That one is a
   * different shape for a good reason: `mine` is its own server parameter there,
   * while here both controls would be writing one `assignee` field, and two
   * controls over one value is the arrangement that ends up disagreeing - press
   * the toggle while the menu says Mari and the screen has to decide which of
   * them is now lying.
   *
   * The operator is offered whether or not the directory holds them. `useStaff`
   * hands back an EMPTY list when its fetch fails (see `_shared/staff.ts`), and
   * a "my tasks" that vanishes exactly when the staff route is down would be
   * missing on the day it is needed most. Their own row is filtered out of the
   * names below so the same person is not offered twice under two labels.
   */
  const assignees = useMemo(
    () => [
      { value: ALL, label: t("tasks.filterAssignee") },
      { value: me.id, label: t("tasks.myTasks") },
      ...staff
        .filter((s) => s.id !== me.id)
        // A person who has left keeps their name and gains the word: they are
        // still on last year's tasks, so filtering by them has to stay possible
        // even though giving them new work does not.
        .map((s) => ({ value: s.id, label: staffOptionLabel(s, t("common.inactive")) })),
    ],
    [staff, me.id, t],
  );

  /** No opinion first, then the vocabulary as the server sorted it. The empty
   *  value is the "any tag" row - see the note on `TaskFilters.tag` for why it
   *  is not `ALL`. */
  const tagOptions = useMemo(
    () => [{ value: "", label: t("projects.filterTag") }, ...tags.map((tag) => ({ value: tag, label: tag }))],
    [tags, t],
  );

  return (
    <Toolbar>
      <SearchField value={filters.q} onChange={(q) => set({ q })} placeholder={t("tasks.search")} />

      {/* The board shows every status as a column of its own, so a status filter
          would be asking the board to hide three of its four columns. */}
      {view === "list" ? (
        <MenuField
          value={filters.status}
          onChange={(status) => set({ status })}
          label={t("projects.statusCol")}
          options={[
            { value: ALL as StatusFilter, label: t("tasks.allStatuses") },
            ...TASK_STATUSES.map((s) => ({ value: s as StatusFilter, label: t(`tasks.status.${s}`) })),
          ]}
        />
      ) : null}

      <MenuField
        value={filters.priority}
        onChange={(priority) => set({ priority })}
        label={t("tasks.priority")}
        options={[
          { value: ALL as PriorityFilter, label: t("tasks.allPriorities") },
          ...PRIORITIES.map((p) => ({ value: p as PriorityFilter, label: t(`tasks.prio.${p}`) })),
        ]}
      />

      <MenuField
        value={filters.projectId}
        onChange={(projectId) => set({ projectId })}
        label={t("tasks.project")}
        options={[
          { value: ALL, label: t("tasks.allProjects") },
          // Archived projects ARE offered here, because their tasks are still on
          // the list this control filters (nothing in the tasks endpoint narrows
          // by the project's archive state), and the mark rides inside the label
          // because `MenuField` draws a row from a string and nothing else - the
          // same constraint `staffOptionLabel` works around for a colleague who
          // has left. Unmarked, a retired project reads as a live one and the
          // operator wonders why the board under it never moves.
          ...projects.map((p) => ({
            value: p.id,
            label: p.archivedAt ? `${p.name} (${t("projects.archivedBadge")})` : p.name,
          })),
        ]}
      />

      {/* Applies to the board as well as the list: "what is moving and what is
          stuck" is a question people ask about one person as often as about the
          whole company. */}
      <MenuField
        value={filters.assignee}
        onChange={(assignee) => set({ assignee })}
        label={t("projects.assignee")}
        options={assignees}
      />

      {/* A picker rather than a menu, and for a reason the other four do not
          have: this list is written by operators, so it grows without limit and
          is hunted through by typing rather than scanned. The options are the
          tags tasks actually carry - archived projects' tasks included, because
          nothing on this page hides those either, and a filter missing an option
          for rows that are visibly listed under it is a control that lies.

          Applies to the board as well as the list. The board's cards draw their
          tags for exactly that reason: a filter whose subject is invisible in
          one of the two views is a filter you cannot check. */}
      <PickerField
        variant="toolbar"
        value={filters.tag}
        onChange={(tag) => set({ tag })}
        options={tagOptions}
        label={t("tasks.tags")}
        placeholder={t("projects.filterTag")}
        searchPlaceholder={t("tasks.tags")}
        // Not "nothing matched your search": an empty vocabulary is the normal
        // state of a CRM where nobody has tagged anything yet, and the menu has
        // to say so rather than look broken.
        emptyLabel={t("palette.empty")}
      />

      <ToolbarGap />
      <PillTabs
        value={view}
        onChange={onView}
        tabs={VIEWS.map((v) => ({ value: v, label: t(VIEW_LABEL[v]) }))}
      />
    </Toolbar>
  );
}
